import React from 'react';
import { useQuery, useQueryClient, useMutation } from 'react-query';
import { useTheme } from '@mui/styles';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';

import {
    fetchAdministrationCategoriesQualifications,
    updateAdministrationCategorie,
    updateAdministrationQualification
} from 'utils/ts-queries';
import { ApiAdministrationCategorie, ApiAdministrationQualification } from 'types/apiReturns';

const QUERY_KEY = 'administrationCategoriesQualifications';

const AdministrationCategoriesQualifications: React.FC = () => {

    const queryClient = useQueryClient();

    const { isLoading, isError, data } = useQuery<ApiAdministrationCategorie[]>({
        queryKey: [QUERY_KEY],
        queryFn: fetchAdministrationCategoriesQualifications
    });

    const [categorieId, setCategorieId] = React.useState<number>(0);
    const [qualificationId, setQualificationId] = React.useState<number>(0);

    React.useEffect(() => {
        const first = data?.[0];
        if (first && categorieId === 0) {
            setCategorieId(first.id);
        }
    }, [data, categorieId]);

    const categorie = React.useMemo(
        () => (data ?? []).find(c => c.id === categorieId),
        [data, categorieId]
    );

    const qualification = React.useMemo(
        () => categorie?.qualifications.find(q => q.id === qualificationId),
        [categorie, qualificationId]
    );

    const handleChangeCategorie = (event: React.ChangeEvent<HTMLInputElement>) => {
        setCategorieId(Number(event.target.value));
        setQualificationId(0);
    };

    const handleChangeQualification = (event: React.ChangeEvent<HTMLInputElement>) => {
        setQualificationId(Number(event.target.value));
    };

    const invalidate = () => queryClient.invalidateQueries([QUERY_KEY]);

    if (isLoading) {
        return <Box sx={{ p: 2 }}><CircularProgress size={24} /></Box>;
    }

    if (isError || !data) {
        return <Typography>Aucune donnée disponible.</Typography>;
    }

    return (
        <Box>
            <Typography gutterBottom variant="h5">
                Administration des catégories &amp; qualifications
            </Typography>

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} mt={2}>
                <Box flex={1}>
                    <CategorieSelectAdmin
                        categories={data}
                        value={categorieId}
                        onChange={handleChangeCategorie}
                    />
                </Box>
                <Box flex={1}>
                    <QualificationSelectAdmin
                        qualifications={categorie?.qualifications ?? []}
                        value={qualificationId}
                        onChange={handleChangeQualification}
                        disabled={!categorie}
                    />
                </Box>
            </Stack>

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} mt={3}>
                <Box flex={1}>
                    {categorie && (
                        <CategoriePanel
                            key={categorie.id}
                            categorie={categorie}
                            onSaved={invalidate}
                        />
                    )}
                </Box>
                <Box flex={1}>
                    {qualification && (
                        <QualificationPanel
                            key={qualification.id}
                            qualification={qualification}
                            onSaved={invalidate}
                        />
                    )}
                </Box>
            </Stack>
        </Box>
    );
};

type CategorieSelectAdminProps = {
    categories: ApiAdministrationCategorie[];
    value: number;
    onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
};

const CategorieSelectAdmin: React.FC<CategorieSelectAdminProps> = ({ categories, value, onChange }) => {

    const aide = categories.find(c => c.id === value)?.aide ?? '';

    return (
        <TextField
            select
            name="categorieId"
            label="Catégorie"
            value={value}
            onChange={onChange}
            helperText={aide}
            fullWidth
        >
            <MenuItem key="first" value={0}>
                <em>Sélectionner une catégorie</em>
            </MenuItem>
            {categories.map(categorie => (
                <MenuItem key={categorie.id} value={categorie.id}>
                    {categorie.libelle}
                </MenuItem>
            ))}
        </TextField>
    );
};

type QualificationSelectAdminProps = {
    qualifications: ApiAdministrationQualification[];
    value: number;
    onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
    disabled: boolean;
};

const QualificationSelectAdmin: React.FC<QualificationSelectAdminProps> = ({ qualifications, value, onChange, disabled }) => {

    const theme = useTheme<any>();
    const aide = qualifications.find(q => q.id === value)?.aide ?? '';

    return (
        <TextField
            select
            name="qualificationId"
            label="Qualification"
            value={value}
            onChange={onChange}
            helperText={aide ? `⚠️ ${aide}` : ''}
            fullWidth
            disabled={disabled}
            FormHelperTextProps={
                aide ? { style: theme?.style?.aideMessage } : undefined
            }
        >
            <MenuItem key="first" value={0}>
                <em>Sélectionner une qualification</em>
            </MenuItem>
            {qualifications.map(qualification => (
                <MenuItem key={qualification.id} value={qualification.id}>
                    {qualification.libelle}
                </MenuItem>
            ))}
        </TextField>
    );
};

type CategoriePanelProps = {
    categorie: ApiAdministrationCategorie;
    onSaved: () => void;
};

const CategoriePanel: React.FC<CategoriePanelProps> = ({ categorie, onSaved }) => {

    const [aide, setAide] = React.useState<string>(categorie.aide ?? '');
    const dirty = aide !== (categorie.aide ?? '');

    const mutation = useMutation({
        mutationFn: () => updateAdministrationCategorie(categorie.id, { aide }),
        onSuccess: onSaved
    });

    return (
        <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                Détails de la catégorie : {categorie.libelle}
            </Typography>

            <Stack spacing={2}>
                <TextField
                    label="Aide"
                    value={aide}
                    onChange={(e) => setAide(e.target.value)}
                    fullWidth
                    multiline
                    minRows={3}
                    maxRows={8}
                />

                <Box display="flex" justifyContent="flex-end">
                    <Button
                        variant="contained"
                        color="secondary"
                        disabled={!dirty || mutation.isLoading}
                        onClick={() => mutation.mutate()}
                    >
                        {mutation.isLoading ? 'Enregistrement…' : 'Enregistrer'}
                    </Button>
                </Box>
            </Stack>
        </Paper>
    );
};

type QualificationPanelProps = {
    qualification: ApiAdministrationQualification;
    onSaved: () => void;
};

const QualificationPanel: React.FC<QualificationPanelProps> = ({ qualification, onSaved }) => {

    const [aide, setAide] = React.useState<string>(qualification.aide ?? '');
    const [impacteQuantiteEnCours, setImpacteQuantiteEnCours] = React.useState<boolean>(qualification.impacteQuantiteEnCours);

    const dirty =
        aide !== (qualification.aide ?? '') ||
        impacteQuantiteEnCours !== qualification.impacteQuantiteEnCours;

    const mutation = useMutation({
        mutationFn: () => updateAdministrationQualification(qualification.id, {
            aide,
            impacteQuantiteEnCours
        }),
        onSuccess: onSaved
    });

    return (
        <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                Détails de la qualification : {qualification.libelle}
            </Typography>

            <Stack spacing={2}>
                <FormControlLabel
                    control={
                        <Switch
                            color="secondary"
                            checked={impacteQuantiteEnCours}
                            onChange={(e) => setImpacteQuantiteEnCours(e.target.checked)}
                        />
                    }
                    label="Impacte la quantité en cours (onglet Commande)"
                />
                <TextField
                    label="Aide"
                    value={aide}
                    onChange={(e) => setAide(e.target.value)}
                    fullWidth
                    multiline
                    minRows={3}
                    maxRows={8}
                />

                <Typography variant="body2" color="text.secondary">
                    Groupe de traitement par défaut : {qualification.groupeTraitementLibelle ?? '—'}
                </Typography>

                <Box display="flex" justifyContent="flex-end">
                    <Button
                        variant="contained"
                        color="secondary"
                        disabled={!dirty || mutation.isLoading}
                        onClick={() => mutation.mutate()}
                    >
                        {mutation.isLoading ? 'Enregistrement…' : 'Enregistrer'}
                    </Button>
                </Box>
            </Stack>
        </Paper>
    );
};

export default AdministrationCategoriesQualifications;
